import React, { useState, useEffect } from 'react'
import { connect, useSelector, useDispatch } from 'react-redux'
import { formValueSelector, reduxForm } from 'redux-form'

import { adaloBackendAxios } from 'utils/io/http/axios'

import { getApp } from 'ducks/apps'
import { createTable, getDatasources } from 'ducks/apps/datasources'
import { getCurrentOrganization, isFeatureEnabled } from 'ducks/organizations'
import { useFunnelTracking } from 'hooks/useFunnelTracking'

import Modal from 'components/Shared/Modal'
import Button from 'components/Shared/Button'
import Icon from 'components/Shared/Icon'

import { SHEETBRIDGE_URL } from 'config'
import history from '../../../history'

import SheetBridgeBridgesForm from './SheetBridgeBridgesForm'
import './index.scss'

export const FORM_NAME = 'sheetBridgeSetup'
const LOADING = 'loading'
const FAILURE = 'failure'

const useBridges = ({ appId, isAuthenticated }) => {
  const [bridges, setBridges] = useState({
    status: 'idle',
    bridges: [],
    error: undefined,
  })

  const fetchBridges = async appId => {
    setBridges(prev => ({ ...prev, status: 'loading', error: undefined }))

    try {
      const sessionId = localStorage.getItem('sheetbridge_session_id')
      const { data } = await adaloBackendAxios.get(
        `/external-databases/sheetbridge/bridges`,
        {
          params: { appId },
          headers: sessionId ? { 'x-sheetbridge-session': sessionId } : {},
        }
      )

      setBridges({
        status: 'loaded',
        bridges: data.bridges || [],
        error: undefined,
      })
    } catch (error) {
      setBridges({ status: 'error', error, bridges: [] })
    }
  }

  // Removed auto-fetch useEffect - bridges should only be fetched:
  // 1. When user clicks "Get My Bridges" button
  // 2. After OAuth callback completes (as continuation of button click)

  return [bridges, { fetchBridges, setBridges }]
}

const SheetBridgeModal = props => {
  const dispatch = useDispatch()
  const selector = formValueSelector(FORM_NAME)

  const { match } = props
  const app = useSelector(state => getApp(state, match.params.appId))
  const datasources = useSelector(state =>
    app ? getDatasources(state, app.id) : undefined
  )
  const datasource = datasources?.[0]

  const currentOrganization = useSelector(getCurrentOrganization)
  const {
    trackClickedSheetBridge,
    trackSheetBridgeGotBridges,
    trackSheetBridgeAddedCollections,
  } = useFunnelTracking()

  const hasExternalCollectionsAccess = useSelector(state =>
    isFeatureEnabled(state, 'customIntegrations')
  )

  // Redirect if user doesn't have external collections access
  useEffect(() => {
    if (hasExternalCollectionsAccess === false) {
      history.push(`/apps/${match.params.appId}/data`)
    }
  }, [hasExternalCollectionsAccess, match.params.appId])

  // Don't render if user doesn't have access
  if (hasExternalCollectionsAccess === false) {
    return null
  }

  const formValueBridges =
    useSelector(state => selector(state, 'bridges')) || {}

  const [step, setStep] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [authState, setAuthState] = useState('idle')
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [checkingAuth, setCheckingAuth] = useState(true)
  const [noBridges, setNoBridges] = useState(false)

  const [bridges, bridgesActions] = useBridges({
    appId: app?.id,
    isAuthenticated,
  })

  useEffect(() => {
    checkAuthStatus()
  }, [])

  // Check for OAuth callback (when user returns from Google OAuth)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const oauthSuccess = urlParams.get('sheetbridge_oauth') === 'success'
    const sessionId = urlParams.get('session_id')

    try {
      console.log('[SheetBridge] OAuth callback check:', {
        oauthSuccess,
        sessionId,
        url: window.location.href,
      })

      if (oauthSuccess) {
        console.log('[SheetBridge] OAuth success detected, fetching bridges')
        console.log('[SheetBridge] Session ID from URL:', sessionId)
        console.log('[SheetBridge] Session ID type:', typeof sessionId)
        console.log('[SheetBridge] Session ID length:', sessionId?.length)

        // Store session ID in localStorage for subsequent requests if available
        if (sessionId) {
          localStorage.setItem('sheetbridge_session_id', sessionId)
          console.log('[SheetBridge] Session ID stored for future requests')
          console.log(
            '[SheetBridge] Verification - stored value:',
            localStorage.getItem('sheetbridge_session_id')
          )
        } else {
          console.error(
            '[SheetBridge] ERROR: No session ID in OAuth callback URL!'
          )
        }

        // Clean up URL immediately to prevent re-triggering
        window.history.replaceState({}, '', window.location.pathname)

        // Add timeout to prevent infinite loading
        const timeout = setTimeout(() => {
          console.error('[SheetBridge] Timeout: bridges fetch took too long')
          setAuthState(FAILURE)
          setIsLoading(false)
          setCheckingAuth(false)
          setStep(0) // Go back to auth step to show error
        }, 10000) // 10 second timeout

        // User just completed OAuth, skip auth check and fetch bridges directly
        handleGetBridges(true)
          .catch(err => {
            console.error('[SheetBridge] Error in OAuth callback:', err)
            setAuthState(FAILURE)
            setStep(0)
          })
          .finally(() => {
            clearTimeout(timeout)
            setCheckingAuth(false)
          })
      }
    } catch (err) {
      console.error('[SheetBridge] Error in OAuth callback useEffect:', err)
      setAuthState(FAILURE)
      setCheckingAuth(false)
      setStep(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkAuthStatus = async () => {
    try {
      setCheckingAuth(true)
      const sessionId = localStorage.getItem('sheetbridge_session_id')
      const { data } = await adaloBackendAxios.get(
        `/external-databases/sheetbridge/auth-status`,
        {
          params: { appId: app?.id },
          headers: sessionId ? { 'X-SheetBridge-Session': sessionId } : {},
          timeout: 5000, // 5 second timeout
        }
      )

      setIsAuthenticated(data.authenticated)
      // Removed automatic setStep(1) - user should explicitly click button
      // even if already authenticated
    } catch (error) {
      console.warn('[SheetBridge] Auth status check failed:', error.message)
      setIsAuthenticated(false)
    } finally {
      setCheckingAuth(false)
    }
  }

  const handleGetBridges = async (skipAuthCheck = false) => {
    try {
      setAuthState(LOADING)
      console.log('[SheetBridge] Starting handleGetBridges', { skipAuthCheck })

      if (currentOrganization?.id) {
        trackClickedSheetBridge(currentOrganization.id).catch(error => {
          console.error('Failed to track SheetBridge click:', error)
        })
      }

      // Skip auth check if we just came back from OAuth
      if (!skipAuthCheck) {
        // Always redirect to OAuth for fresh authentication
        console.log('[SheetBridge] Redirecting to OAuth for authentication')

        const authUrlResponse = await adaloBackendAxios.get(
          `/external-databases/sheetbridge/auth-url`,
          {
            params: { appId: app?.id },
            timeout: 5000,
          }
        )

        if (authUrlResponse.data?.authUrl) {
          console.log(
            '[SheetBridge] Redirecting to:',
            authUrlResponse.data.authUrl
          )
          window.location.href = authUrlResponse.data.authUrl

          return
        } else {
          throw new Error('Failed to get authentication URL')
        }
      }

      // User is authenticated, fetch bridges
      console.log('[SheetBridge] User authenticated, fetching bridges')
      const sessionId = localStorage.getItem('sheetbridge_session_id')
      console.log('[SheetBridge] Session ID from localStorage:', sessionId)
      console.log('[SheetBridge] Session ID length:', sessionId?.length)
      console.log(
        '[SheetBridge] Will send header:',
        sessionId ? { 'x-sheetbridge-session': sessionId } : 'NO HEADER'
      )

      if (!sessionId) {
        console.error('[SheetBridge] WARNING: No session ID available!')
      }

      const bridgesResponse = await adaloBackendAxios.get(
        `/external-databases/sheetbridge/bridges`,
        {
          params: { appId: app?.id },
          headers: sessionId ? { 'x-sheetbridge-session': sessionId } : {},
          timeout: 10000, // 10 seconds for bridge fetching
        }
      )

      console.log(
        '[SheetBridge] Bridges response status:',
        bridgesResponse.status
      )
      console.log(
        '[SheetBridge] Bridges response headers:',
        bridgesResponse.headers
      )

      console.log('[SheetBridge] Bridges response:', bridgesResponse.data)
      const bridgesData = bridgesResponse.data?.bridges || []

      console.log('[SheetBridge] Raw bridges data:', bridgesData)
      console.log('[SheetBridge] Number of bridges:', bridgesData.length)
      console.log(
        '[SheetBridge] Full response JSON:',
        JSON.stringify(bridgesResponse.data, null, 2)
      )

      // Log each bridge's details
      bridgesData.forEach((bridge, idx) => {
        console.log(`[SheetBridge] Bridge ${idx}:`, {
          id: bridge.id,
          name: bridge.name,
          sheetsCount: bridge.sheets?.length || 0,
          sheets: bridge.sheets,
        })
      })

      // Check if there are any sheets across all bridges
      const totalSheets = bridgesData.reduce(
        (sum, bridge) => sum + (bridge.sheets?.length || 0),
        0
      )

      console.log('[SheetBridge] Total sheets across all bridges:', totalSheets)

      if (totalSheets === 0) {
        console.log(
          '[SheetBridge] No sheets found in any bridge - showing error'
        )
        setNoBridges(true)
        setIsAuthenticated(true)
        setAuthState('idle')
        setStep(0)

        return
      }

      console.log('[SheetBridge] Setting bridges and advancing to selection')
      bridgesActions.setBridges({
        status: 'loaded',
        bridges: bridgesData,
        error: undefined,
      })

      // Advance to selection step
      setIsAuthenticated(true)
      setNoBridges(false)
      setStep(1)
      setAuthState('idle')

      if (currentOrganization?.id) {
        trackSheetBridgeGotBridges(currentOrganization.id).catch(error => {
          console.error('Failed to track SheetBridge got bridges:', error)
        })
      }
    } catch (error) {
      console.error('[SheetBridge] Error in handleGetBridges:', error)
      console.error('[SheetBridge] Error response:', error.response?.data)
      console.error('[SheetBridge] Error status:', error.response?.status)
      console.error('[SheetBridge] Error config:', {
        url: error.config?.url,
        headers: error.config?.headers,
        params: error.config?.params,
      })

      // Check if it's an authentication error (401/403)
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log(
          '[SheetBridge] Auth error detected - session may have expired'
        )
        console.log(
          '[SheetBridge] Current session ID:',
          localStorage.getItem('sheetbridge_session_id')
        )

        // Clear the invalid session
        localStorage.removeItem('sheetbridge_session_id')
        console.log('[SheetBridge] Cleared invalid session from localStorage')

        // Try to get OAuth URL and redirect
        try {
          const authUrlResponse = await adaloBackendAxios.get(
            `/external-databases/sheetbridge/auth-url`,
            {
              params: { appId: app?.id },
              timeout: 5000,
            }
          )

          if (authUrlResponse.data?.authUrl) {
            console.log(
              '[SheetBridge] Redirecting to OAuth:',
              authUrlResponse.data.authUrl
            )
            window.location.href = authUrlResponse.data.authUrl

            return
          }
        } catch (authError) {
          console.error('[SheetBridge] Failed to get auth URL:', authError)
        }
      }

      console.log('[SheetBridge] Setting error state')
      setAuthState(FAILURE)
      bridgesActions.setBridges({ status: 'error', error, bridges: [] })
      console.error('[SheetBridge] Failed to get bridges:', error)
    }
  }

  const handleClose = () => {
    const { match } = props
    const { appId } = match.params
    const url = `/apps/${appId}/data`

    history.push(url)
  }

  const handleBridgesSubmit = async () => {
    setIsLoading(true)

    // Import field introspection utilities
    const { getFields } = await import('utils/external-collections')

    const selectedSheetKeys = Object.keys(formValueBridges).filter(
      key => formValueBridges[key]
    )

    try {
      if (selectedSheetKeys.length) {
        const { getTableId } = await import('@adalo/utils')
        const { stripTrailingSlash, appendURLBinding } = await import(
          'utils/bindings'
        )

        for (const sheetKey of selectedSheetKeys) {
          // sheetKey format: "bridgeId-sheetId"
          // Bridge IDs are UUIDs with dashes, so split from the last dash
          const lastDashIndex = sheetKey.lastIndexOf('-')
          const bridgeId = sheetKey.substring(0, lastDashIndex)
          const sheetId = sheetKey.substring(lastDashIndex + 1)
          const bridge = bridges.bridges.find(b => b.id === bridgeId)
          if (!bridge) {
            continue
          }

          const sheet = (bridge.sheets || []).find(s => s.id === sheetId)
          if (!sheet) {
            continue
          }

          const baseURL = stripTrailingSlash(sheet.apiUrl)
          const normalizedURL = baseURL

          let fields = {}
          let orderedFields = []

          // Fetch sample data to introspect fields
          try {
            // Build auth array for the request
            const auth = sheet.authHeaderEncrypted
              ? [
                  {
                    type: 'header',
                    name: sheet.authHeaderName || 'Authorization',
                    value: sheet.authHeaderEncrypted,
                    encrypted: true,
                  },
                ]
              : []

            // Use QS to serialize auth array (same as test button)
            const QS = await import('qs')
            const query = QS.default.stringify({
              url: baseURL,
              auth,
              method: 'get',
            })

            const { data: testResponse } = await adaloBackendAxios.get(
              `/test-api-endpoint?${query}`
            )

            if (testResponse.success && testResponse.response?.data) {
              const results = Array.isArray(testResponse.response.data)
                ? testResponse.response.data
                : [testResponse.response.data]

              if (results.length > 0) {
                // Use getFields utility to derive field structure
                const [derivedFields, derivedOrderedFields] = getFields(results)
                fields = derivedFields
                orderedFields = derivedOrderedFields
              }
            }
          } catch (error) {
            console.warn(
              'Failed to introspect API, using minimal fields:',
              error
            )

            // Fallback to minimal field set
            fields.id = {
              name: 'id',
              type: 'text',
              isPrimaryField: true,
            }
            orderedFields.push('id')
          }

          const endpoints = {
            list: {
              enabled: true,
              method: 'get',
              url: baseURL,
            },
            detail: {
              enabled: true,
              method: 'get',
              url: appendURLBinding(normalizedURL, '/{{id}}'),
            },
            create: {
              enabled: true,
              method: 'post',
              url: baseURL,
            },
            update: {
              enabled: true,
              method: 'put',
              url: appendURLBinding(normalizedURL, '/{{id}}'),
            },
            delete: {
              enabled: true,
              method: 'delete',
              url: appendURLBinding(normalizedURL, '/{{id}}'),
            },
          }

          // Build auth configuration from sheet's auth header
          // Mark as encrypted so database service will decrypt it
          const auth = sheet.authHeaderEncrypted
            ? [
                {
                  type: 'header',
                  name: sheet.authHeaderName || 'Authorization',
                  value: sheet.authHeaderEncrypted,
                  encrypted: true,
                },
              ]
            : []

          const tableId = getTableId()

          const tableData = {
            id: tableId,
            name: sheet.name,
            type: 'api',
            tableType: 'api',
            baseURL,
            endpoints,
            fields,
            orderedFields,
            auth,
          }

          dispatch(createTable(app.id, datasource.id, tableData))
        }
      }

      setIsLoading(false)

      if (currentOrganization?.id) {
        trackSheetBridgeAddedCollections(currentOrganization.id).catch(
          error => {
            console.error(
              'Failed to track SheetBridge added collections:',
              error
            )
          }
        )
      }

      // Auto-close the modal after successful collection creation
      handleClose()
    } catch (err) {
      setIsLoading(false)
      console.error('Failed to create collections:', err)
    }
  }

  const handleNext = e => {
    if (e?.preventDefault) e.preventDefault()

    if (step === 1) {
      return handleBridgesSubmit()
    }

    if (step === 2) {
      return handleClose()
    }
  }

  const getStepLabel = () => {
    switch (step) {
      case 1:
        return 'Connect'
      case 2:
        return 'Done'
      default:
        return 'Next'
    }
  }

  const renderContent = () => {
    switch (step) {
      case 0:
        return (
          <div className="sheetbridge-auth-step">
            <h2>Connect to SheetBridge</h2>
            <p>
              Connect your Google Sheets to SheetBridge and create instant API
              endpoints for your Adalo app.
            </p>
            {checkingAuth ? (
              <div className="checking-auth">
                <p>Checking account status...</p>
              </div>
            ) : (
              <>
                {noBridges && (
                  <div className="error-message">
                    No bridges found for this account. Please create a
                    SheetBridge account or add bridges, then try again.
                  </div>
                )}
                {/* <p className="have-account-text">Have an account?</p> */}
                <div className="auth-buttons">
                  <Button
                    teal
                    onClick={() => handleGetBridges()}
                    loading={authState === LOADING}
                    disabled={authState === LOADING}
                  >
                    Get My Bridges
                  </Button>
                  <p className="have-account-text">
                    Don't have an account?{' '}
                    <a
                      href={SHEETBRIDGE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="signup-link"
                    >
                      Sign up to SheetBridge
                    </a>
                  </p>
                </div>
                {authState === FAILURE && (
                  <div className="error-message">
                    Failed to connect. Please try again.
                  </div>
                )}
              </>
            )}
          </div>
        )
      case 1:
        return (
          <SheetBridgeBridgesForm
            bridges={bridges.bridges}
            status={bridges.status}
            datasource={datasource}
          />
        )
      case 2:
        return (
          <div className="sheetbridge-success-step">
            <Icon type="check-circle" big color="green" />
            <h2>Success!</h2>
            <p>Your SheetBridge collections have been added successfully.</p>
          </div>
        )
    }
  }

  const leftButtons = [
    <Button
      key="back"
      text
      onClick={step === 0 ? handleClose : () => setStep(step - 1)}
      type="button"
      disabled={isLoading || authState === LOADING}
    >
      {step === 0 ? 'Close' : 'Back'}
    </Button>,
  ]

  const stepLabel = getStepLabel()
  const canProceed =
    step === 1
      ? bridges.status === 'loaded' &&
        Object.keys(formValueBridges).some(k => formValueBridges[k])
      : false

  return (
    <Modal size="lg" scrolling className="sheetbridge-modal">
      <div>
        <Modal.Header>
          <div className="sheetbridge-modal-header">
            <div className="sheetbridge-logo-header">
              <span className="sheet-text">Sheet</span>
              <svg
                className="bridge-icon"
                viewBox="0 0 2048 2048"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="#14b8a6"
                  d="M727.25 755.969v369.251h-15.218V755.969z"
                />
                <path
                  fill="#14b8a6"
                  d="M1335.97 768.174v336.966h-15.22V768.174z"
                />
                <path
                  fill="#0d9488"
                  d="M1791.99 1200H256.008v-98.66H1791.99z"
                />
                <path fill="#14b8a6" d="M919.999 900.418V1104h-16V900.418z" />
                <path fill="#14b8a6" d="M1144 900.418V1104h-16V900.418z" />
                <path fill="#14b8a6" d="M1712 857.805V1104h-16V857.805z" />
                <path fill="#14b8a6" d="M351.999 882.638V1104h-16V882.638z" />
                <path
                  fill="#2dd4bf"
                  d="m1772.42 935.86 8.773-30.831c-79.038-30.886-229.945-215.017-265.969-289.703l-10.797-22.386-15.81 19.224c-84.625 102.884-324.186 299.648-464.608 299.648-170.567 0-345.723-213.288-479.087-315.905-75.185 90.795-169.866 266.828-278.097 309.122l8.774 30.831c103.07-38.646 216.416-201.393 265.304-297.634 145.676 108.258 298.877 305.34 482.98 305.34h.251c134.672 0 373.81-174.633 457.637-273.614l15.566-18.378 10.907 21.473c33.854 66.64 193.684 236.384 264.175 262.814z"
                />
                <path
                  fill="#0d9488"
                  d="M1536.01 608.302V1456h-64V608.302L1504 592z"
                />
                <path
                  fill="#0d9488"
                  d="M576.005 608.302V1456h-64.001V608.302L544.001 592z"
                />
              </svg>
              <span className="bridge-text">Bridge</span>
            </div>
            <p className="sheetbridge-modal-subtitle">
              Connect your Google Sheets as external collections
            </p>
          </div>
        </Modal.Header>
        <Modal.Content>{renderContent()}</Modal.Content>
        <Modal.Actions leftButtons={leftButtons}>
          {step === 1 && (
            <Button
              teal
              type="button"
              disabled={!canProceed || isLoading}
              onClick={handleNext}
              loading={isLoading}
            >
              {stepLabel}
            </Button>
          )}
        </Modal.Actions>
      </div>
    </Modal>
  )
}

const SheetBridgeForm = reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
})(SheetBridgeModal)

export default connect((state, { match }) => {
  const app = getApp(state, match.params.appId)

  return {
    app,
    initialValues: {},
  }
})(SheetBridgeForm)
